import React, { useEffect, useRef, useState } from "react";
import { Form, FormInstance, Select, theme, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  IPartner,
  PartnerType,
} from "../../../../../interfaces/partner.interface";
import { getPartnersFromServer } from "../../../partnersApi";
import { WizardMode } from "../wizardSteps";

const { Text } = Typography;

// UI cards map onto the backend PartnerType enum. Resellers may carry a vendor link.
export type PartnerTypeOptionId =
  | "SoftwareVendor"
  | "Reseller"
  | "FullServicePartner"
  | "FullServiceWithResellers";

interface TypeOption {
  id: PartnerTypeOptionId;
  partnerType: PartnerType;
  title: string;
  description: string;
  showsVendorField: boolean;
}

export const PARTNER_TYPE_OPTIONS: TypeOption[] = [
  {
    id: "SoftwareVendor",
    partnerType: PartnerType.SoftwareVendor,
    title: "Software Vendor",
    description: "Technical integration partner.",
    showsVendorField: false,
  },
  {
    id: "Reseller",
    partnerType: PartnerType.Reseller,
    title: "Reseller",
    description: "Reseller, optionally linked to a software vendor.",
    showsVendorField: true,
  },
  {
    id: "FullServicePartner",
    partnerType: PartnerType.FullServicePartner,
    title: "Full Service Partner",
    description: "Technical integration partner and its own reseller.",
    showsVendorField: false,
  },
  {
    id: "FullServiceWithResellers",
    partnerType: PartnerType.FullServiceWithResellers,
    title: "Full Service Partner with Resellers",
    description:
      "Technical integration partner with its own reseller and external resellers.",
    showsVendorField: false,
  },
];

export const optionIdFromPartner = (
  partner?: Partial<IPartner>
): PartnerTypeOptionId | undefined => {
  if (!partner?.partnerType) return undefined;
  const match = PARTNER_TYPE_OPTIONS.find(
    (o) => o.partnerType === partner.partnerType
  );
  return match?.id;
};

export const partnerFieldsFromOption = (
  optionId: PartnerTypeOptionId,
  softwareVendorId?: string
): { partnerType: PartnerType; softwareVendorId?: string } => {
  const option = PARTNER_TYPE_OPTIONS.find((o) => o.id === optionId)!;
  return {
    partnerType: option.partnerType,
    softwareVendorId: option.showsVendorField ? softwareVendorId : undefined,
  };
};

interface PartnerTypeStepProps {
  form: FormInstance;
  mode: WizardMode;
  partner?: Partial<IPartner>;
}

export default function PartnerTypeStep({
  form,
  partner,
}: PartnerTypeStepProps) {
  const { token } = theme.useToken();
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorOptions, setVendorOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const selectedOptionId = Form.useWatch(
    "partnerTypeOption",
    form
  ) as PartnerTypeOptionId;
  const selectedOption = PARTNER_TYPE_OPTIONS.find(
    (o) => o.id === selectedOptionId
  );

  const loadVendors = async (search: string, includeVendorId?: string) => {
    try {
      setVendorsLoading(true);
      const response = await getPartnersFromServer({
        partnerType: "SoftwareVendor,FullServiceWithResellers",
        name: search,
        partnerId: includeVendorId || undefined,
        currentPage: 1,
        pageSize: 50,
      });
      setVendorOptions(
        (response?.data || []).map((vendor) => ({
          value: vendor._id,
          label: vendor.name,
        }))
      );
    } finally {
      setVendorsLoading(false);
    }
  };

  // Seed once per mount. The wizard re-mounts this step with a draft-derived
  // `partner` after a tab switch, so a fresh mount restores the user's pick;
  // guarding avoids re-seeding (and re-fetching vendors) on every card click.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    const optionId = optionIdFromPartner(partner);
    const vendorId =
      typeof partner?.softwareVendorId === "string"
        ? partner.softwareVendorId
        : undefined;
    form.setFieldsValue({
      partnerTypeOption: optionId,
      softwareVendorId: vendorId,
    });
    loadVendors("", vendorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner, form]);

  const selectCard = (id: PartnerTypeOptionId) => {
    const option = PARTNER_TYPE_OPTIONS.find((o) => o.id === id)!;
    form.setFieldsValue({ partnerTypeOption: id });
    if (!option.showsVendorField)
      form.setFieldsValue({ softwareVendorId: undefined });
  };

  return (
    <Form form={form} layout="vertical" requiredMark autoComplete="off">
      <Form.Item
        name="partnerTypeOption"
        label="Partner Type"
        rules={[{ required: true, message: "Please select a partner type" }]}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginTop: 4,
          }}
        >
          {PARTNER_TYPE_OPTIONS.map((option) => {
            const selected = option.id === selectedOptionId;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => selectCard(option.id)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  textAlign: "left",
                  padding: "14px 16px",
                  borderRadius: 12,
                  cursor: "pointer",
                  background: selected
                    ? token.colorPrimaryBg
                    : token.colorBgContainer,
                  border: `1px solid ${
                    selected ? token.colorPrimary : token.colorBorder
                  }`,
                  boxShadow: selected
                    ? `0 0 0 1px ${token.colorPrimary}`
                    : "none",
                  transition: "border-color 0.15s, background 0.15s",
                }}
              >
                <span
                  style={{
                    flex: "0 0 auto",
                    marginTop: 2,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: `2px solid ${
                      selected ? token.colorPrimary : token.colorBorder
                    }`,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {selected && (
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: token.colorPrimary,
                      }}
                    />
                  )}
                </span>
                <span
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <Text strong style={{ lineHeight: 1.2 }}>
                    {option.title}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 13, lineHeight: 1.35 }}
                  >
                    {option.description}
                  </Text>
                </span>
              </button>
            );
          })}
        </div>
      </Form.Item>

      {selectedOption?.showsVendorField && (
        <Form.Item
          name="softwareVendorId"
          label="Software Vendor"
          extra="Optionally pick the software vendor whose system this partner resells."
          style={{ maxWidth: 460 }}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={loadVendors}
            loading={vendorsLoading}
            options={vendorOptions}
            placeholder="Search or select a vendor"
            suffixIcon={<SearchOutlined />}
          />
        </Form.Item>
      )}
    </Form>
  );
}
